'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  try {
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payload, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateFuelTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/fuel-type/search');
};

export const useFuelTypeList = (page = 0, rowsPerPage = 1000, searchKey = '', searchValue = '') => {
  const knownFields = ['name', 'code', 'category', 'uom_type', 'description'];

  const payload = useMemo(() => {
    let actualKey = 'name';
    let actualVal = '';

    if (typeof searchValue === 'string' && searchValue.trim() !== '') {
      actualKey = searchKey && searchKey !== 'all' ? searchKey : 'name';
      actualVal = searchValue.trim();
    } else if (
      typeof searchKey === 'string' &&
      searchKey.trim() !== '' &&
      searchKey !== 'all' &&
      (searchValue === undefined || searchValue === null)
    ) {
      actualKey = 'name';
      actualVal = searchKey.trim();
    } else if (typeof searchKey === 'string' && searchKey.trim() !== '' && !knownFields.includes(searchKey) && searchValue === '') {
      actualKey = 'name';
      actualVal = searchKey.trim();
    }

    const fb = new FilterBuilder().setPaging(page * rowsPerPage, rowsPerPage).setOrder(['id'], false);

    if (actualVal !== '') {
      fb.like(actualKey, actualVal, 'AND');
    }

    return fb.build();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchKey, searchValue]);

  const { data, error } = useSWR(['/fuel-type/search', payload], fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  });

  const responseBody = data?.data ?? data;
  const rows = Array.isArray(responseBody) ? responseBody : (responseBody?.data ?? []);
  const total = Array.isArray(responseBody)
    ? responseBody.length
    : (responseBody?.totalRecord ?? responseBody?.totalRecords ?? responseBody?.dataSize ?? 0);

  return {
    data: rows,
    totalRecord: total,
    isLoading: !error && !data,
    isError: error
  };
};

export const getFuelTypeById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/fuel-type/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    // Fallback using search and FilterBuilder if direct GET fails or needs complex join
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/fuel-type/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);

    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch fuel type by id:', err);
    throw err;
  }
};

export const createFuelType = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/fuel-type/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelTypeList();
    return response.data;
  } catch (err) {
    console.error('Error creating fuel type:', err);
    throw err;
  }
};

export const updateFuelType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/fuel-type/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelTypeList();
    mutate(`/fuel-type/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating fuel type:', err);
    throw err;
  }
};

export const deleteFuelType = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/fuel-type/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelTypeList();
    return response.data;
  } catch (err) {
    console.error('Error deleting fuel type:', err);
    throw err;
  }
};
